import { findRow, formatPrice } from "@/data/pricing";
import { specialisationFaqs } from "@/data/specialisationFaqs";
import { createFileRoute, Link } from "@tanstack/react-router";
import { BookFitCall } from "@/components/site/BookFitCall";
import { fitCallUrl } from "@/lib/booking";
import { m } from "framer-motion";
import { MarginWaterfall } from "@/components/diagrams/MarginWaterfall";
import { ArrowRight, TrendingUp } from "lucide-react";
import {
  canonicalLinks,
  ogUrl,
  jsonLd,
  serviceSchema,
  breadcrumbSchema,
  faqPageSchema,
  webPageSchema, absoluteUrl, ogImage } from "@/lib/seo";
import { PhotoBand } from "@/components/site/PhotoBand";
import operatingScorecardImg from "@/assets/photos/operating-scorecard.webp";
import { NextStep } from "@/components/site/NextStep";
import { DeliveredThrough } from "@/components/site/CrossLinks";

const answerPrice = (name: string) => formatPrice(findRow(name));

const PATH = "/specialisations/pricing-and-revenue-management";
const TITLE =
  "Pricing and revenue management for Australian SMEs";
const DESCRIPTION =
  "Price architecture, margin waterfalls, discount and rebate control, revenue leakage and price-increase execution for founder-led Australian businesses.";

const CAREER_NOTE =
  "These results come from Adam's operating career before Ajility, inside the businesses themselves.";

const proof = [
  {
    figure: ">$6M in first-year revenue growth from an annual labour and service category price review",
    org: "A 249-store national retailer",
  },
  {
    figure: "8% volume growth and $2M in annualised profit from a new tyre pricing strategy",
    org: "The same retailer",
    note: "When volume and margin move together, the price architecture is right.",
  },
  {
    figure:
      "~$2.5M p.a. in revenue management benefits from a consolidated NPV and pricing guidance tool, winner of the company-wide Innov8 competition",
    org: "A $5 billion beverage business",
  },
  {
    figure:
      "~$1M p.a. in revenue management benefits and $300K p.a. in FTE savings from a new sales-contract pricing tool",
    org: "The same business",
  },
  {
    figure: "A cross-channel diagnostic of $10M of processed customer credits",
    org: "The same business",
  },
  {
    figure: "A national pricing platform serving 20,000+ customers",
    org: "The same business",
  },
];

/** Folded in from the former standalone revenue leakage page. */
const leakage = [
  {
    title: "Unearned discounts",
    desc: "Off-list prices granted for a reason that has since expired, or for no recorded reason at all, still applying invoice after invoice.",
  },
  {
    title: "Unmanaged rebates",
    desc: "Rebates accrued and paid on volumes, ranges or terms that were never achieved, because nobody reconciles the entitlement to the outcome.",
  },
  {
    title: "Credits and claims",
    desc: "Customer credits and claims processed on trust, without challenge, evidence or a threshold above which someone has to approve them.",
  },
  {
    title: "Pricing overrides",
    desc: "Manual price changes entered at the point of sale or invoice, outside the price file, with no approval trail and no reporting.",
  },
  {
    title: "Contract non-compliance",
    desc: "Agreed terms, minimum volumes, service levels and price escalations that sit in the contract and never make it into the billing.",
  },
  {
    title: "Recovery and prevention",
    desc: "What can be recovered now, what can only be stopped from here, and the controls and reporting that keep each one closed.",
  },
];

const covers = [
  {
    title: "Price architecture",
    desc: "How every line, tier and customer segment relates to every other one, so a change in one place does not quietly undo margin somewhere else.",
  },
  {
    title: "Margin waterfall",
    desc: "The path from list price to the cash that actually lands, step by step, with every deduction named and owned.",
  },
  {
    title: "Unit economics",
    desc: "What a job, a unit or a customer really earns once labour, materials, freight and service recovery are loaded properly.",
  },
  {
    title: "Discount and rebate control",
    desc: "Approval thresholds, rebate terms and the reporting that makes an off-list price a decision rather than a habit.",
  },
  {
    title: "Revenue leakage",
    desc: "Unbilled work, uncharged variations, expired rate cards, credit notes and rounding, found and closed.",
  },
  {
    title: "Price-increase execution",
    desc: "Sequencing, segmenting and communicating an increase, with the scripts and the tracking to see how volume responds.",
  },
];

const faqs = specialisationFaqs["pricing-and-revenue-management"];

export const Route = createFileRoute("/specialisations/pricing-and-revenue-management")({
  head: () => ({
    meta: [
      { name: "twitter:card", content: "summary_large_image" },
      { title: `${TITLE} | Ajility` },
      { name: "description", content: DESCRIPTION },
      { property: "og:title", content: `${TITLE} | Ajility` },
      { property: "og:description", content: DESCRIPTION },
      { property: "og:type", content: "website" },
      { name: "twitter:title", content: `${TITLE} | Ajility` },
      { name: "twitter:description", content: DESCRIPTION },
      ...ogImage("specialisations-pricing-and-revenue-management", `${TITLE} by Ajility`),
      ogUrl(PATH),
    ],
    links: canonicalLinks(PATH),
    scripts: [
      jsonLd(webPageSchema({ mainEntityId: `${absoluteUrl(PATH)}#service`, path: PATH, name: `${TITLE} | Ajility`, description: DESCRIPTION })),
      jsonLd(
        breadcrumbSchema([
          { name: "Expertise", path: "/specialisations" },
          { name: "Pricing and revenue management", path: PATH },
        ]),
      ),
      jsonLd(
        serviceSchema({
          name: "Pricing and revenue management",
          description: DESCRIPTION,
          path: PATH,
          offerId: "pricing-margin-review",
          slug: "pricing-and-revenue-management",
          serviceType: "Pricing and revenue management",
        }),
      ),
      jsonLd(faqPageSchema(PATH, faqs, `${absoluteUrl(PATH)}#service`)),
    ],
  }),
  component: PricingSpecialisation,
});

function PricingSpecialisation() {
  return (
    <>
      <section className="relative overflow-hidden bg-midnight-deep pt-40 pb-24 text-frost">
        <div className="absolute inset-0 grid-lines opacity-20" />
        <m.div
          initial={{ y: 20 }}
          animate={{ y: 0 }}
          transition={{ duration: 0.6 }}
          className="relative mx-auto max-w-7xl px-5 sm:px-8"
        >
          <div className="grid h-14 w-14 place-items-center rounded-2xl border border-white/10 bg-white/[0.04] text-steel-on-dark">
            <TrendingUp className="h-6 w-6" aria-hidden="true" />
          </div>
          <span className="eyebrow mt-8 block on-dark">Expertise: pricing and revenue management</span>
          <h1 className="mt-5 max-w-4xl font-serif text-4xl leading-[1.05] sm:text-5xl lg:text-6xl text-balance">
            Most businesses have never tested their own prices.
          </h1>
          <p className="mt-6 max-w-3xl text-lg text-frost/75 text-pretty">
            Pricing is the commercial expertise Ajility leads with. Prices are usually set
            once, indexed occasionally, and then defended by memory. <Link to="/insights/the-6m-price-review" className="underline decoration-white/40 underline-offset-4 hover:decoration-frost">A structured review of price
            architecture, margin and leakage</Link> is the fastest work available in most founder-led
            businesses, because the revenue is already there.
          </p>
          <div className="mt-10 flex flex-wrap items-start gap-4">
            <BookFitCall
              placement="specialisations_pricing_revenue_hero"
              buttonClassName="group inline-flex items-center gap-2 rounded-full bg-frost px-6 py-3 text-sm font-semibold text-midnight-deep transition hover:bg-white"
              noteClassName="mt-2 text-sm font-medium text-frost/70 hover:text-frost"
            >
              Book a fit call
              <ArrowRight className="h-4 w-4 transition group-hover:translate-x-1" aria-hidden="true" />
            </BookFitCall>
            <Link
              to="/packages"
              className="inline-flex rounded-full border border-white/15 px-6 py-3 text-sm font-medium text-frost/85 transition hover:border-white/30 hover:text-frost"
            >
              See the Pricing and Margin Review
            </Link>
          </div>
        </m.div>
      </section>

      <PhotoBand
        src={operatingScorecardImg}
        alt="A printed operating scorecard beside a whiteboard tracking grid with markers on the tray"
        priority
      />

      <section className="bg-background py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <span className="eyebrow">What the work covers</span>
          <h2 className="mt-4 font-serif text-3xl text-foreground sm:text-4xl text-balance">
            Six places margin is made and lost
          </h2>
          <div className="mt-12">
            <MarginWaterfall />
          </div>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {covers.map((c, i) => (
              <m.div
                key={c.title}
                initial={{ y: 16 }}
                whileInView={{ y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="card-panel"
              >
                <h3 className="font-serif text-xl text-foreground">{c.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-charcoal">{c.desc}</p>
              </m.div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-midnight py-24 text-frost">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <span className="eyebrow on-dark">Track record</span>
          <h2 className="mt-4 font-serif text-3xl sm:text-4xl text-balance">
            Pricing work delivered in senior commercial roles
          </h2>
          <p className="mt-4 max-w-3xl text-steel-on-dark">{CAREER_NOTE}</p>
          <ul className="mt-12 grid gap-6 md:grid-cols-2">
            {proof.map((p) => (
              <li
                key={p.figure}
                className="rounded-2xl border border-white/10 bg-white/[0.04] p-7"
              >
                <p className="font-serif text-lg leading-snug text-frost">{p.figure}</p>
                <p className="mt-3 text-sm text-steel-on-dark">{p.org}</p>
                {p.note ? <p className="mt-3 text-sm text-frost/75">{p.note}</p> : null}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Revenue leakage and recovery, folded in from its own page */}
      <section className="bg-surface py-24">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <span className="eyebrow">Where it leaks</span>
          <h2 className="mt-4 font-serif text-3xl text-foreground sm:text-4xl text-balance">
            Revenue leakage and recovery
          </h2>
          <p className="mt-4 max-w-3xl text-charcoal">
            Leakage is not lost sales. It is work already won and already delivered, quietly reduced
            on the way to the bank. Founder-led businesses rarely see it, because no single instance
            is large enough to notice: it arrives as one credit, one override, one rebate, spread
            across thousands of transactions and several systems, and the P&amp;L reports only the
            net result. A review finds it, sizes it and closes it.
          </p>
          <div className="mt-12 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
            {leakage.map((c, i) => (
              <m.div
                key={c.title}
                initial={{ y: 16 }}
                whileInView={{ y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.05 }}
                className="card-panel"
              >
                <h3 className="font-serif text-xl text-foreground">{c.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-charcoal">{c.desc}</p>
              </m.div>
            ))}
          </div>
          <p className="mt-10 max-w-3xl text-charcoal">
            Most of the work is done on extracted data rather than on your team&apos;s time.
            Findings are ranked by annual value, by how quickly each one can be stopped, and by
            whether it is recoverable or only preventable. Anything still leaking is closed first.
          </p>
        </div>
      </section>

      {/* Tender pricing and re-tender retention */}
      <section className="bg-background py-20">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <span className="eyebrow">Tenders and re-tenders</span>
          <h2 className="mt-4 font-serif text-3xl text-foreground">
            Pricing a bid to win without buying the work
          </h2>
          <p className="mt-4 text-charcoal">
            A tender is a pricing decision made under time pressure with incomplete information, and
            it is where margin is most often given away for good. The work is to establish the true
            cost to serve before a rate is written, decide which parts of the bid carry the margin
            and which are there to win it, and set the walk-away position in advance so it holds on
            the day. Defending a key account at re-tender is the same discipline in reverse: knowing
            what the account actually earns, what the incumbent advantage is genuinely worth, and
            which concessions can be traded for term or volume rather than simply conceded.
          </p>
        </div>
      </section>

      <section className="bg-mist py-20">
        <div className="mx-auto max-w-3xl px-5 text-center sm:px-8">
          <span className="eyebrow">Buy it on its own</span>
          <h2 className="mt-4 font-serif text-3xl text-foreground">Pricing and Margin Review</h2>
          <p className="mt-4 text-charcoal">
            {`Ajility's Pricing and Margin Review is ${answerPrice("Pricing and Margin Review")} and takes three to six weeks, subject to size, schedule and availability. Where it is not yet clear that margin is the constraint, start with the Operating Diagnostic, ${answerPrice("Operating Diagnostic")}.`}{" "}
            This area of expertise sits inside every Fractional COO engagement, and it can also be
            bought as a single priced piece of work. The scope and the fee are published on the
            packages page.
          </p>
          <p className="mt-4 text-charcoal">Read <Link to="/insights/$slug" params={{ slug: "revenue-leakage" }} className="underline underline-offset-4">revenue leakage</Link> for the practical questions behind this work.</p>
          <p className="mt-4 text-charcoal">Read <Link to="/insights/$slug" params={{ slug: "card-surcharge-ban-pricing" }} className="underline underline-offset-4">The card surcharge ban: how to price the fee back in without losing customers</Link> for the practical questions behind this work.</p>
          <Link
            to="/packages"
            className="mt-8 inline-flex items-center gap-2 rounded-full bg-midnight px-6 py-3 text-sm font-semibold text-primary-foreground transition hover:bg-midnight-deep"
          >
            See the Pricing and Margin Review
            <ArrowRight className="h-4 w-4" aria-hidden="true" />
          </Link>
        </div>
      </section>

      <section className="bg-background py-24">
        <div className="mx-auto max-w-3xl px-5 sm:px-8">
          <span className="eyebrow">FAQ</span>
          <h2 className="mt-4 font-serif text-3xl text-foreground sm:text-4xl">Common questions</h2>
          <dl className="mt-10 space-y-6">
            {faqs.map((f) => (
              <div key={f.q} className="card-panel">
                <dt>
                  <h3 className="font-serif text-lg text-foreground">{f.q}</h3>
                </dt>
                <dd className="mt-3 text-sm leading-relaxed text-charcoal">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <DeliveredThrough slug="pricing-and-revenue-management" />

      <NextStep
        heading="Start with the free Operating Scan"
        body="21 plain statements about how the business actually runs, answered from Not us to Always. In about six minutes you see a provisional score naming the weakest areas, then choose whether you want a conversation."
        primaryLabel="Take the free Operating Scan"
        primaryTo="/scan"
        secondaryLabel="Book a fit call"
        secondaryTo={fitCallUrl("specialisations_pricing_revenue_footer")}
        secondaryNote={
          <Link to="/contact" className="text-sm font-medium text-frost/70 hover:text-frost">
            Or send a note instead
          </Link>
        }
      />
    </>
  );
}
